import React, { useState, useEffect } from 'react';
import PerformanceBadge from '../PerformanceBadge';
import NoteTypeTag from '../NoteTypeTag';
import AttachmentGallery from './AttachmentGallery';
import PersonalNoteCard from './PersonalNoteCard';
import PersonalNoteModal from './PersonalNoteModal';
import AccessControlPanel from './AccessControlPanel';
import LoadingSkeleton from './LoadingSkeleton';
import MilestoneCelebration from '../MilestoneCelebration';
import RichTextDisplay from './RichTextDisplay';
import FormattedContent from './FormattedContent';
import UnassignedPocketNotesSection from './UnassignedPocketNotesSection';
import { personalNotes, pocket } from '../api';
import '../styles/noteUrls.css';
import './PersonalNotesTab.css';

/**
 * Unified NotesTab - Combines Coach Notes and Personal Notes with toggle
 */
const NotesTab = ({
  notesSource = 'coach', // 'coach' or 'personal'
  onSourceChange,
  onTabChange, // switches PlayerView between 'notes' and 'starred'
  myNotes,
  loading,
  showNotePopup,
  setShowNotePopup,
  popupNote,
  setPopupNote,
  readNotes,
  markAsRead,
  starredNotes,
  toggleStar,
  showMilestoneCelebration,
  setShowMilestoneCelebration,
  milestoneData,
  loadingNotes,
  user,
  activeTab, // 'notes' or 'starred'
  allPlayers = [] // List of coach's students for assigning unassigned notes
}) => {
  // ✅ PHASE 3: Personal Notes state
  const [personalNotesData, setPersonalNotesData] = useState([]);
  const [personalNotesLoading, setPersonalNotesLoading] = useState(false);
  const [personalNotesError, setPersonalNotesError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [showAccessPanel, setShowAccessPanel] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);

  // 🎙️ POCKET NOTES: Load and merge with regular notes
  const [pocketNotes, setPocketNotes] = useState([]);
  const [pocketNotesLoading, setPocketNotesLoading] = useState(false);

  // ✅ PHASE 3: Load personal notes when tab switches to personal
  useEffect(() => {
    if (notesSource === 'personal') {
      loadPersonalNotes();
    }
  }, [notesSource]);

  // 🎙️ Load Pocket notes when viewing coach notes (for players)
  useEffect(() => {
    // Loaded whatever the source toggle says, so the Starred count (which can
    // include starred Pocket notes) doesn't change when switching to Personal.
    if (user?._id && allPlayers.length === 0) {
      // Only load for players (coaches have allPlayers, players don't)
      loadPocketNotes();
    } else {
      setPocketNotes([]);
    }
  }, [user?._id, allPlayers.length]);

  // When viewing starred notes, ALWAYS show coach notes only (never personal notes)
  useEffect(() => {
    if (activeTab === 'starred') {
      onSourceChange('coach');
    }
  }, [activeTab]);

  // Filter coach notes based on activeTab
  // When activeTab === 'starred', show only starred notes
  // When activeTab === 'notes', show all notes
  // 🎙️ Merge Pocket notes with regular notes for players
  const allCoachNotes = myNotes ? [...myNotes, ...pocketNotes] : pocketNotes;
  // The starred ids are kept in localStorage, so they can outlive the note (deleted,
  // or starred under another account). The badge must count only the starred notes
  // that can actually be shown, or it promises notes the Starred list doesn't have.
  const visibleStarredCount = (allCoachNotes && starredNotes)
    ? allCoachNotes.filter(note => starredNotes.includes(note._id)).length
    : 0;
  const displayedNotes = activeTab === 'starred'
    ? (allCoachNotes && starredNotes ? allCoachNotes.filter(note => starredNotes.includes(note._id)) : [])
    : allCoachNotes;

  const loadPersonalNotes = async () => {
    setPersonalNotesLoading(true);
    setPersonalNotesError(null);
    try {
      const response = await personalNotes.getMyNotes();
      setPersonalNotesData(response.data.notes || []);
    } catch (err) {
      console.error('❌ Error loading personal notes:', err);
      setPersonalNotesError('Failed to load personal notes');
    } finally {
      setPersonalNotesLoading(false);
    }
  };

  // 🎙️ Load Pocket notes and transform to regular note format
  const loadPocketNotes = async () => {
    setPocketNotesLoading(true);
    try {
      // Players: fetch own notes (no coachId filter)
      // Coaches: pass coachId for filtering
      const response = await pocket.getPlayerNotes(user?._id, allPlayers.length > 0 ? user?._id : null);
      const transformed = (response.data.playerNotes || []).map(pNote => ({
        _id: pNote.id,
        text: pNote.autoPocketSummary || 'No summary available',
        type: 'pocket_recording', // Custom type for styling
        createdAt: pNote.createdAt,
        isPocketNote: true,
        originalPocketData: pNote
      }));
      setPocketNotes(transformed);
    } catch (err) {
      console.error('❌ Error loading Pocket notes:', err);
      setPocketNotes([]);
    } finally {
      setPocketNotesLoading(false);
    }
  };

  const handleCreateNote = async (title, content, category) => {
    try {
      await personalNotes.createNote(title, content, category);
      setShowModal(false);
      setEditingNote(null);
      await loadPersonalNotes();
    } catch (err) {
      console.error('❌ Error creating note:', err);
      alert('Failed to create note: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdateNote = async (title, content, category) => {
    try {
      await personalNotes.updateNote(editingNote._id, title, content, category);
      setShowModal(false);
      setEditingNote(null);
      await loadPersonalNotes();
    } catch (err) {
      console.error('❌ Error updating note:', err);
      alert('Failed to update note: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note? This action cannot be undone.')) {
      return;
    }

    try {
      await personalNotes.deleteNote(noteId);
      await loadPersonalNotes();
    } catch (err) {
      console.error('❌ Error deleting note:', err);
      alert('Failed to delete note: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleOpenAccessPanel = (noteId) => {
    setSelectedNoteId(noteId);
    setShowAccessPanel(true);
  };

  const handleCloseAccessPanel = () => {
    setShowAccessPanel(false);
    setSelectedNoteId(null);
  };

  // Filter personal notes
  const filteredPersonalNotes = personalNotesData.filter(note => {
    if (filterType === 'all') return true;
    if (filterType === 'private') return note.isPrivate;
    if (filterType === 'shared') return !note.isPrivate;
    return true;
  });

  const privateCount = personalNotesData.filter(n => n.isPrivate).length;
  const sharedCount = personalNotesData.filter(n => !n.isPrivate).length;

  return (
    <div>
      {/* ✅ PHASE 3: Notes Source Toggle */}
      {/* Coach Notes / Personal Notes / Starred - Starred used to be its own
          menu tab; it lives here now, next to the other two. */}
      {(

        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          // #f5f5f5 (not #f6f6f6) deliberately - the dark-theme override
          // sweep recognizes this exact shade (rgb(245,245,245)) and darkens
          // it under dark theme. The inactive tab's text color (#7f8c8d) is
          // already correctly flipped light by that same sweep, so this
          // background needs to flip with it - otherwise the inactive tab
          // ends up with light text on a background that never changed,
          // which is nearly invisible.
          background: '#f5f5f5',
          padding: '8px',
          borderRadius: '8px',
          width: 'fit-content'
        }}>
          <button
            onClick={() => { if (onTabChange) onTabChange('notes'); onSourceChange('coach'); }}
            style={{
              padding: '8px 16px',
              background: notesSource === 'coach' && activeTab !== 'starred' ? 'white' : 'transparent',
              color: notesSource === 'coach' && activeTab !== 'starred' ? '#2f4ba2' : '#7f8c8d',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              transition: 'all 0.2s ease'
            }}
          >
            📝 Coach Notes
          </button>
          {onTabChange && (
            <button
              onClick={() => onTabChange('starred')}
              style={{
                padding: '8px 16px',
                background: activeTab === 'starred' ? 'white' : 'transparent',
                color: activeTab === 'starred' ? '#2f4ba2' : '#7f8c8d',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              ⭐ Starred
              {visibleStarredCount > 0 && (
                <span style={{
                  background: '#f39c12',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '1px 6px',
                  fontSize: '10px',
                  fontWeight: '700',
                  lineHeight: '1.3'
                }}>
                  {visibleStarredCount}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Display header when viewing Starred tab */}
      {activeTab === 'starred' && (
        <div style={{
          marginBottom: '20px',
          fontSize: '16px',
          fontWeight: '600',
          color: '#f39c12'
        }}>
          ⭐ Starred Notes ({displayedNotes.length})
        </div>
      )}

      {/* ✅ PHASE 3: Coach Notes Display */}
      {notesSource === 'coach' && (
        <>
          {/* 🎙️ POCKET API: Show unassigned Pocket notes for coaches ONLY */}
          {allPlayers.length > 0 && (
            <UnassignedPocketNotesSection allPlayers={allPlayers} userId={user?._id} />
          )}

          {loadingNotes || loading || pocketNotesLoading ? (
            <div style={{ padding: '20px 0' }}>
              <LoadingSkeleton type="card" count={3} />
            </div>
          ) : !displayedNotes || displayedNotes.length === 0 ? (
            <div style={{
              background: activeTab === 'starred'
                ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                : 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
              padding: '80px 40px',
              borderRadius: '24px',
              textAlign: 'center',
              color: 'white',
              boxShadow: activeTab === 'starred'
                ? '0 20px 60px rgba(243, 156, 18, 0.3)'
                : '0 20px 60px rgba(47, 75, 162, 0.3)'
            }}>
              <div style={{ fontSize: '80px', marginBottom: '24px' }}>
                {activeTab === 'starred' ? '⭐' : '📭'}
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}>
                {activeTab === 'starred' ? 'No starred notes yet' : 'No notes yet'}
              </div>
              <div style={{ fontSize: '18px', opacity: 0.9 }}>
                {activeTab === 'starred' ? 'Star notes to bookmark them' : 'Your coach will send you feedback here'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {displayedNotes
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((note, index, array) => {
                  const lessonNotesBefore = array
                    .slice(index)
                    .filter(n => n.noteType === 'lesson').length;

                  const isMilestoneBoundary =
                    note.noteType === 'lesson' &&
                    lessonNotesBefore > 0 &&
                    lessonNotesBefore % 10 === 0 &&
                    (index === array.length - 1 || array[index + 1]?.noteType !== 'lesson' ||
                     array.slice(index + 1).filter(n => n.noteType === 'lesson').length % 10 !== 0);

                  const isRead = readNotes && Array.isArray(readNotes) && readNotes.includes(note._id);
                  const isStarred = starredNotes && Array.isArray(starredNotes) && starredNotes.includes(note._id);

                  return (
                    <React.Fragment key={note._id}>
                      <div
                        data-note-id={note._id}
                        style={{
                          // Both states use a dark background so the note text
                          // can stay white regardless of read/unread. Unread
                          // is a plain 'white' keyword (auto-darkened to the
                          // app's cool navy by the dark-theme override); read
                          // is an explicit warm-dark gradient (gradients bypass
                          // that override) so it still reads as "seen" via a
                          // gold-brown tint rather than a light background.
                          background: isRead
                            ? 'linear-gradient(135deg, #3a3226 0%, #2b241a 100%)'
                            : 'white',
                          padding: '24px',
                          borderRadius: '20px',
                          boxShadow: isRead
                            ? '0 2px 12px rgba(253, 187, 45, 0.15)'
                            : '0 4px 20px rgba(47, 75, 162, 0.15)',
                          position: 'relative',
                          overflow: 'hidden',
                          border: isRead
                            ? '2px solid #f9e79f'
                            : '2px solid rgba(47, 75, 162, 0.2)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: note.performanceBadge
                            ? {
                                excellent: 'linear-gradient(90deg, #FFD700 0%, #FFA500 100%)',
                                good: 'linear-gradient(90deg, #27ae60 0%, #2ecc71 100%)',
                                average: 'linear-gradient(90deg, #3498db 0%, #5dade2 100%)',
                                needs_work: 'linear-gradient(90deg, #e67e22 0%, #f39c12 100%)',
                                keep_trying: 'linear-gradient(90deg, #9b59b6 0%, #8e44ad 100%)'
                              }[note.performanceBadge]
                            : 'linear-gradient(90deg, #2f4ba2 0%, #14246b 100%)'
                        }} />

                        {note.performanceBadge && (
                          <PerformanceBadge level={note.performanceBadge} size="diagonal" />
                        )}

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '16px',
                          gap: '8px',
                          paddingRight: '60px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: 1,
                            minWidth: 0,
                            flexWrap: 'wrap'
                          }}>
                            {isRead && (
                              <div style={{
                                fontSize: '16px',
                                color: '#27ae60'
                              }}>
                                ✓
                              </div>
                            )}

                            <div style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              // brighter gold for the read card now that its
                              // background is dark, not light cream
                              color: isRead ? '#e8c766' : '#7f8c8d'
                            }}>
                              {new Date(note.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>

                            {note.noteType && (
                              <NoteTypeTag type={note.noteType} size="small" audience="player" />
                            )}
                          </div>
                        </div>

                        <div style={{
                          fontSize: '17px',
                          fontWeight: '500',
                          lineHeight: '1.6',
                          // Read card background is a dark gradient (gradients
                          // bypass the dark-theme override, so it's always
                          // dark) - white text is correct there regardless of
                          // theme. Unread card background is a plain 'white'
                          // keyword, only auto-darkened when dark theme is
                          // active, so its text needs a normal dark color that
                          // the override's rgb() sweep can flip light once the
                          // card itself goes dark - not a hardcoded light one.
                          color: isRead ? '#f5f5f7' : '#333',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          paddingRight: '60px'
                        }}>
                          {note.isPocketNote || note.noteType === 'pocket_recording' ? (
                            <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              <RichTextDisplay content={note.text} />
                            </div>
                          ) : (
                            <FormattedContent content={note.text} />
                          )}
                        </div>

                        {/* Photo Attachments - Display photos if they exist */}
                        {note.attachments && note.attachments.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <AttachmentGallery
                              attachments={note.attachments}
                              isCoach={false}
                              noteId={note._id}
                            />
                          </div>
                        )}

                        <div style={{
                          marginTop: '16px',
                          display: 'flex',
                          gap: '8px',
                          paddingTop: '12px',
                          borderTop: `1px solid ${isRead ? '#f9e79f' : '#f0f0f0'}`
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPopupNote(note);
                              setShowNotePopup(true);
                            }}
                            style={{
                              flex: 1,
                              padding: '10px 16px',
                              background: isRead
                                ? 'linear-gradient(135deg, #ed8d3e 0%, #be5307 100%)'
                                : 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: '600',
                              boxShadow: isRead
                                ? '0 2px 8px rgba(212, 106, 13, 0.88)'
                                : '0 2px 8px rgba(47, 75, 162, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            👁️ View Full Note
                          </button>

                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await navigator.clipboard.writeText(note.text);
                                e.target.innerHTML = '✓';
                                e.target.style.background = 'linear-gradient(135deg, #27ae60 0%, #229954 100%)';
                                setTimeout(() => {
                                  e.target.innerHTML = '📋';
                                  e.target.style.background = 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)';
                                }, 2000);
                              } catch (err) {
                                console.error('Failed to copy:', err);
                                alert('Failed to copy text');
                              }
                            }}
                            style={{
                              padding: '10px 16px',
                              background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '16px',
                              fontWeight: '600',
                              boxShadow: '0 2px 8px rgba(149, 165, 166, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '50px',
                              cursor: 'pointer'
                            }}
                          >
                            📋
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleStar(note._id);
                            }}
                            style={{
                              padding: '10px 16px',
                              background: isStarred
                                ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                                : 'rgba(0, 0, 0, 0.05)',
                              color: isStarred ? 'white' : '#95a5a6',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '18px',
                              fontWeight: '600',
                              boxShadow: isStarred
                                ? '0 2px 8px rgba(243, 156, 18, 0.3)'
                                : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '50px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {isStarred ? '⭐' : '☆'}
                          </button>
                        </div>
                      </div>

                      {isMilestoneBoundary && (
                        <div style={{
                          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                          padding: '20px 24px',
                          borderRadius: '16px',
                          textAlign: 'center',
                          boxShadow: '0 8px 24px rgba(255, 215, 0, 0.3)',
                          border: '2px solid #FFD700',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
                            pointerEvents: 'none'
                          }} />
                          <div style={{
                            position: 'relative',
                            zIndex: 1
                          }}>
                            <div style={{
                              fontSize: '48px',
                              marginBottom: '8px'
                            }}>
                              🏆
                            </div>
                            <div style={{
                              fontSize: '22px',
                              fontWeight: '800',
                              color: 'white',
                              textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              marginBottom: '4px'
                            }}>
                              {lessonNotesBefore} Lessons Milestone!
                            </div>
                            <div style={{
                              fontSize: '14px',
                              color: 'rgba(255,255,255,0.95)',
                              fontWeight: '600'
                            }}>
                              Amazing progress! Keep it up! 🎉
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          )}
        </>
      )}

      {/* ✅ PHASE 3: Personal Notes Display */}
      {notesSource === 'personal' && activeTab !== 'starred' && (
        <div className="personal-notes-tab">
          <div className="notes-header">
            <div>
              <h3>📔 My Personal Notes</h3>
              <p className="notes-subtext">
                Keep personal coaching notes and choose which coaches can view them
              </p>
            </div>
            <button
              className="btn-create-note"
              onClick={() => {
                setEditingNote(null);
                setShowModal(true);
              }}
            >
              + New Note
            </button>
          </div>

          {personalNotesError && (
            <div className="notes-error">
              ❌ {personalNotesError}
            </div>
          )}

          {!personalNotesLoading && personalNotesData.length > 0 && (
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                onClick={() => setFilterType('all')}
              >
                All ({personalNotesData.length})
              </button>
              <button
                className={`filter-btn ${filterType === 'private' ? 'active' : ''}`}
                onClick={() => setFilterType('private')}
              >
                Private ({privateCount})
              </button>
              <button
                className={`filter-btn ${filterType === 'shared' ? 'active' : ''}`}
                onClick={() => setFilterType('shared')}
              >
                Shared ({sharedCount})
              </button>
            </div>
          )}

          {personalNotesLoading && (
            <div className="notes-loading">
              Loading your notes...
            </div>
          )}

          {!personalNotesLoading && personalNotesData.length === 0 && (
            <div className="empty-state">
              <p>No personal notes yet</p>
              <p>Create your first note to get started</p>
            </div>
          )}

          {!personalNotesLoading && filteredPersonalNotes.length > 0 && (
            <div className="notes-feed">
              {filteredPersonalNotes.map(note => (
                <PersonalNoteCard
                  key={note._id}
                  note={note}
                  onEdit={() => {
                    setEditingNote(note);
                    setShowModal(true);
                  }}
                  onDelete={() => handleDeleteNote(note._id)}
                  onManageAccess={() => handleOpenAccessPanel(note._id)}
                />
              ))}
            </div>
          )}

          {!personalNotesLoading && personalNotesData.length > 0 && filteredPersonalNotes.length === 0 && (
            <div className="empty-state">
              <p>No {filterType} notes found</p>
            </div>
          )}

          {showModal && (
            <PersonalNoteModal
              note={editingNote}
              onSave={editingNote ? handleUpdateNote : handleCreateNote}
              onCancel={() => {
                setShowModal(false);
                setEditingNote(null);
              }}
            />
          )}

          {showAccessPanel && (
            <AccessControlPanel
              noteId={selectedNoteId}
              onClose={handleCloseAccessPanel}
              onAccessChanged={loadPersonalNotes}
            />
          )}
        </div>
      )}

      {/* ❌ REMOVED: NotePopup should ONLY be rendered from PlayerView, not here
          Having it in both places caused duplicate modals to appear simultaneously */}
    </div>
  );
};

export default NotesTab;
